/* eslint-disable @typescript-eslint/no-explicit-any */

import { TFunction } from "i18next";

import { CircularProgress } from "@mui/material";

import { faChartSimple } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { TableColumn } from "@/components/Table/Table";

import { ReactComponent as trash } from "@assets/icons/create-list/trash.svg";
import { ReactComponent as downloadIcon } from "@assets/icons/download.svg";

interface CsvEnrichmentSourceColumnsParams {
	t: TFunction<"translation">;
	downloadIsLoading?: number | null;
	handleDownload?: (row: any) => void;
	handleDeleteModal?: (row: any) => void;
	handleStatistics?: (row: any) => void;
}

/**
 * Columns for Excel/CSV sources in the "Added sources" table: name (with a "screening on"
 * badge when the source screens job titles before enrichment, PREC-3536) + download/delete.
 */
export const getCsvEnrichmentSourceColumns = ({
	t,
	downloadIsLoading,
	handleDownload,
	handleDeleteModal,
	handleStatistics,
}: CsvEnrichmentSourceColumnsParams): TableColumn[] => {
	return [
		{
			title: "name",
			uid: "name",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center gap-2">
					{row.name}
					{!!row.screenTitles && (
						<span className="rounded-md bg-blue-action-200 px-2 py-0.5 text-xs font-medium text-brand-light">
							{t("csvEnrichment.titleGate.screeningOn")}
						</span>
					)}
				</div>
			),
		},
		{
			title: "",
			uid: "action",
			allowExclude: false,
			hideable: false,
			moveable: false,
			show: true,

			render: (row: any) => (
				<div className="flex items-center grow justify-end">
					{/* PREC-3870: what this file produced. Only offered once the source has rows to
					    report on — an upload that has not been processed has nothing to show. */}
					{!!handleStatistics && (
						<button
							className="flex items-center gap-1.5 cursor-pointer rounded-md px-2 py-1 text-xs font-semibold text-text-meta hover:bg-gray-100"
							data-test={`xls-statistics-action-${row.id}`}
							title={t("csvEnrichment.statsModal.title")}
							type="button"
							onClick={() => handleStatistics(row)}
						>
							<Icon className="h-3.5 w-3.5" icon={faChartSimple} />
							{t("csvEnrichment.statsModal.title")}
						</button>
					)}
					{(!downloadIsLoading || downloadIsLoading !== row.id) && (
						<SvgIcon
							className="min-w-[20px] h-[20px] cursor-pointer text-gray-700 ml-1"
							svgIcon={downloadIcon}
							onClick={() => {
								!downloadIsLoading && handleDownload?.(row);
							}}
						/>
					)}
					{downloadIsLoading && downloadIsLoading === row.id && (
						<div className=" w-[20px] h-[20px] flex items-center justify-center ml-1">
							<CircularProgress size={20} />
						</div>
					)}
					<SvgIcon
						className="w-[24px] h-[24px] cursor-pointer text-gray-700 ml-1"
						svgIcon={trash}
						onClick={() => {
							handleDeleteModal?.(row);
						}}
					/>
				</div>
			),
		},
	];
};
